/**
 * Shared claim-shape mapper for the Duende BFF `/auth/user` response.
 *
 * Duende returns claims as an array of `{ type, value }` pairs (with
 * repeated `type`s for multi-valued claims like roles). The rule
 * evaluator in `shared/authRule.ts` expects a `Record<claim, value>`
 * with multi-valued claims represented as arrays — this helper bridges
 * the two. It is used both by `server/middleware/auth.ts` (Nitro
 * gate, SSR) and `app/composables/useAuth.ts` (client hydration), so
 * server and client see the same claim shape.
 */
export interface UserClaim {
  readonly type: string;
  readonly value: string | number | boolean;
}

/**
 * Build a `{ type: value }` record from the BFF claims array.
 *
 * Values are normalised to `string` (single-valued) or `string[]`
 * (multi-valued, e.g. repeated `role` entries) so the output shape never
 * depends on claim cardinality. Duende can emit numeric/boolean claim values;
 * coercing here keeps every consumer — the `authRule` evaluator (which
 * already `String()`s both sides), the SSR auth context, and client
 * `useAuth` state — seeing one consistent type. Returns `null` when the array
 * is empty or carries no non-empty string `sub`; the BFF only emits `sub` for
 * authenticated sessions, so this doubles as the "really authenticated?"
 * check.
 */
export function mapUserClaims(
  claimsArray: readonly UserClaim[] | null | undefined,
): Record<string, string | string[]> | null {
  if (!claimsArray?.length) return null;

  const claims: Record<string, string | string[]> = {};
  let hasSub = false;

  for (const claim of claimsArray) {
    const { type, value: raw } = claim;
    // Gate `hasSub` on the raw value so a numeric/boolean `sub` can't be
    // coerced into passing the authentication check.
    if (type === "sub" && typeof raw === "string" && raw.length > 0) {
      hasSub = true;
    }
    const value = String(raw);
    const existing = claims[type];
    if (existing === undefined) {
      claims[type] = value;
    } else if (Array.isArray(existing)) {
      existing.push(value);
    } else {
      claims[type] = [existing, value];
    }
  }

  return hasSub ? claims : null;
}
